import React from 'react';
import { EsporaiTooltip } from 'components';

/**
 * CompactConnectionBadge
 * Mobile-optimized connection status badge showing only icon + status dot
 * Used in MobileTopNavBar to show Google/Meta connection status at a glance
 *
 * Props:
 *   - connected: boolean - Whether the platform is connected
 *   - icon: React Component - Icon component (GoogleIcon or MetaIcon)
 *   - label: string - Platform name ("Google Ads" or "Meta Ads")
 *   - onClick: function - Handler for when badge is clicked
 *   - size: number - Icon size in pixels (default 18)
 *   - disabled: boolean - No interaction (e.g. flujo obligatorio de conexión)
 *   - disabledReason: string - Tooltip/aria when disabled (e.g. chrome lock copy)
 */
const CompactConnectionBadge = ({
  connected,
  icon: Icon,
  label,
  onClick,
  size = 18,
  disabled = false,
  disabledReason = '',
}) => {
  const defaultTooltip = connected ? `${label} conectado` : `Conectar ${label}`;
  const tooltipContent =
    disabled && disabledReason ? disabledReason : defaultTooltip;

  const badge = (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`
        relative flex items-center justify-center
        p-2 rounded-lg transition-all duration-200 touch-manipulation min-w-[40px] min-h-[40px] flex-shrink-0
        ${disabled
          ? 'opacity-50 cursor-not-allowed border border-white/10 bg-white/5'
          : `cursor-pointer active:scale-95 ${connected
            ? 'bg-white/10 border border-white/20 hover:bg-white/15'
            : 'bg-white/5 border border-white/10 hover:bg-white/15 hover:border-amber-400/40'
          }`
        }
      `}
      title={tooltipContent}
      aria-label={tooltipContent}
      aria-disabled={disabled}
    >
      <span className="relative">
        <Icon
          width={size}
          height={size}
          className={connected ? 'opacity-100' : 'opacity-50'}
        />
        <span
          className={`
            absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full
            ring-2 ring-slate-900/80
            ${connected ? 'bg-emerald-400' : 'bg-red-500'}
          `}
          aria-hidden
        />
      </span>
    </button>
  );

  return (
    <EsporaiTooltip content={tooltipContent} position="bottom">
      {disabled ? (
        <span className="inline-flex" title={tooltipContent}>
          {badge}
        </span>
      ) : (
        badge
      )}
    </EsporaiTooltip>
  );
};

export default CompactConnectionBadge;
